export const BLOCK_STYLE_VERSION = 1;
export const STYLE_BREAKPOINT = 640;

export const STYLE_PROPERTIES = Object.freeze({
  color: 'color', backgroundColor: 'background-color', backgroundImage: 'background-image',
  fontFamily: 'font-family', fontSize: 'font-size', fontWeight: 'font-weight', fontStyle: 'font-style',
  lineHeight: 'line-height', letterSpacing: 'letter-spacing', textIndent: 'text-indent', textColumns: 'column-count', textAlign: 'text-align', textTransform: 'text-transform', textDecoration: 'text-decoration',
  marginTop: 'margin-top', marginRight: 'margin-right', marginBottom: 'margin-bottom', marginLeft: 'margin-left',
  paddingTop: 'padding-top', paddingRight: 'padding-right', paddingBottom: 'padding-bottom', paddingLeft: 'padding-left', gap: 'gap',
  width: 'width', height: 'height', minWidth: 'min-width', maxWidth: 'max-width', minHeight: 'min-height', maxHeight: 'max-height', flexGrow: 'flex-grow', aspectRatio: 'aspect-ratio',
  borderColor: 'border-color', borderWidth: 'border-width', borderStyle: 'border-style', borderRadius: 'border-radius',
  borderTopWidth: 'border-top-width', borderRightWidth: 'border-right-width', borderBottomWidth: 'border-bottom-width', borderLeftWidth: 'border-left-width',
  boxShadow: 'box-shadow', opacity: 'opacity', overflow: 'overflow', objectFit: 'object-fit', objectPosition: 'object-position',
});

export const ADVANCED_STYLE_PROPERTIES = Object.freeze(['opacity', 'overflow', 'boxShadow', 'minWidth', 'maxWidth', 'minHeight', 'maxHeight', 'letterSpacing', 'textTransform', 'textDecoration']);

const LENGTH = /^(?:0|auto|var\(--[a-z0-9-_]+\)|-?(?:\d+|\d*\.\d+)(?:px|rem|em|%|vw|vh))$/i;
const NUMBER = /^(?:0|(?:\d+|\d*\.\d+))$/;
const COLOR = /^(?:#[0-9a-f]{3,8}|(?:rgb|hsl)a?\([\d\s.,%+-]+\)|var\(--[a-z0-9-_]+\)|transparent|currentColor|inherit)$/i;
const SAFE_FONT = /^[a-z0-9 ,"'-]+$/i;
const POSITION = /^(?:left|center|right|top|bottom|(?:\d+|\d*\.\d+)%)(?:\s+(?:left|center|right|top|bottom|(?:\d+|\d*\.\d+)%))?$/i;
const ENUMS = {
  flexGrow: ['0', '1'],
  fontStyle: ['normal', 'italic', 'oblique'], textAlign: ['left', 'center', 'right', 'justify', 'start', 'end'],
  textTransform: ['none', 'uppercase', 'lowercase', 'capitalize'], textDecoration: ['none', 'underline', 'line-through'],
  borderStyle: ['none', 'solid', 'dashed', 'dotted', 'double'], overflow: ['visible', 'hidden', 'auto', 'scroll', 'clip'], objectFit: ['fill', 'contain', 'cover', 'none', 'scale-down'],
};
const LENGTH_KEYS = new Set(['fontSize','letterSpacing','textIndent','marginTop','marginRight','marginBottom','marginLeft','paddingTop','paddingRight','paddingBottom','paddingLeft','gap','width','height','minWidth','maxWidth','minHeight','maxHeight','borderWidth','borderRadius','borderTopWidth','borderRightWidth','borderBottomWidth','borderLeftWidth']);
const COLOR_KEYS = new Set(['color', 'backgroundColor', 'borderColor']);

const unsafe = (value) => /(?:javascript\s*:|expression\s*\(|url\s*\(|[{};<>])/i.test(value);
export function styleValueOptions(property) { return [...(ENUMS[property] || (property === 'textColumns' ? ['1','2','3','4','5','6'] : property === 'fontWeight' ? ['normal','bold','100','200','300','400','500','600','700','800','900'] : []))]; }

export function styleValueUnits(property) { return LENGTH_KEYS.has(property) || property === 'textIndent' || property === 'lineHeight' ? ['px','rem','em','%','vw','vh'].filter(unit => normalizeStyleValue(property, `1${unit}`) !== null) : []; }

export function normalizeStyleValue(property, input) {
  if (!(property in STYLE_PROPERTIES) || input === null || input === undefined || input === '') return null;
  const value = String(input).trim();
  if (!value || value.length > 240 || unsafe(value)) return null;
  if (property === 'textIndent') {
    const match = value.match(/^(\d+(?:\.\d+)?|\.\d+)(px|rem|em|vw|vh)$/i);
    if (!match) return null;
    return Number(match[1]) <= (match[2].toLowerCase() === 'px' ? 100 : 10) ? value : null;
  }
  if (property === 'textColumns') return /^[1-6]$/.test(value) ? value : null;
  if (property === 'flexGrow') return /^(?:0|1)$/.test(value) ? value : null;
  if (property === 'maxHeight' && value === 'none') return value;
  if (LENGTH_KEYS.has(property)) return LENGTH.test(value) ? value : null;
  if (COLOR_KEYS.has(property)) return COLOR.test(value) ? value : null;
  if (ENUMS[property]) return ENUMS[property].includes(value) ? value : null;
  if (property === 'backgroundImage') return /^linear-gradient\((?:[^();]|rgba?\([\d\s.,%+-]+\)|hsla?\([\d\s.,%+-]+\)){1,220}\)$/i.test(value) ? value : null;
  if (property === 'fontFamily') return ['var(--font-body)', 'var(--font-heading)'].includes(value) || SAFE_FONT.test(value) ? value : null;
  if (property === 'fontWeight') return /^(?:normal|bold|[1-9]00)$/.test(value) ? value : null;
  if (property === 'lineHeight') return NUMBER.test(value) || LENGTH.test(value) ? value : null;
  if (property === 'aspectRatio') return /^(?:auto|\d+(?:\.\d+)?\s*\/\s*\d+(?:\.\d+)?)$/.test(value) ? value : null;
  if (property === 'opacity') return NUMBER.test(value) && Number(value) <= 1 ? value : null;
  if (property === 'objectPosition') return POSITION.test(value) ? value : null;
  if (property === 'boxShadow') return value === 'none' || (/^[#(),.\-\w\s%]+$/.test(value) && /(?:px|rem|em)/.test(value)) ? value : null;
  return null;
}

const cleanMap = (input, allowed) => {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return {};
  const result = {};
  for (const [property, value] of Object.entries(input)) {
    if (!allowed.has(property)) continue;
    const normalized = normalizeStyleValue(property, value);
    if (normalized !== null) result[property] = normalized;
  }
  return result;
};

export function normalizeStyleSettings(input, supportedProperties = Object.keys(STYLE_PROPERTIES)) {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return null;
  const allowed = new Set(supportedProperties.filter((property) => property in STYLE_PROPERTIES));
  const advancedAllowed = new Set([...allowed].filter((property) => ADVANCED_STYLE_PROPERTIES.includes(property)));
  const base = cleanMap(input.base, allowed);
  const mobile = cleanMap(input.mobile, allowed);
  const advanced = cleanMap(input.advanced, advancedAllowed);
  if (!Object.keys(base).length && !Object.keys(mobile).length && !Object.keys(advanced).length) return null;
  return { version: BLOCK_STYLE_VERSION, ...(Object.keys(base).length ? { base } : {}), ...(Object.keys(mobile).length ? { mobile } : {}), ...(Object.keys(advanced).length ? { advanced } : {}) };
}

export function stylePresentation(input, supportedProperties = Object.keys(STYLE_PROPERTIES)) {
  const settings = normalizeStyleSettings(input, supportedProperties);
  if (!settings) return { settings: null, className: '', style: '', styleObject: {} };
  const base = { ...(settings.base || {}), ...(settings.advanced || {}) };
  const declarations = Object.entries(base).map(([key, value]) => `${STYLE_PROPERTIES[key]}:${value}`);
  const styleObject = Object.fromEntries(Object.entries(base).map(([key, value]) => [key === 'textColumns' ? 'columnCount' : key, value]));
  const classes = ['mts-block-styled'];
  for (const [key, value] of Object.entries(settings.mobile || {})) {
    declarations.push(`--mts-mobile-${STYLE_PROPERTIES[key]}:${value}`);
    styleObject[`--mts-mobile-${STYLE_PROPERTIES[key]}`] = value;
    classes.push(`mts-mobile-${key.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`)}`);
  }
  return { settings, className: classes.join(' '), style: declarations.join(';') || undefined, styleObject };
}
